import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  UserGroupIcon,
} from '@heroicons/react/24/outline';

const C = {
  teal: '#00B4D8', success: '#10B981', warning: '#F59E0B',
  error: '#EF4444', dark: '#1F2937', grey: '#6B7280', border: '#E5E7EB',
  bg: '#F9FAFB',
};

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const PACKAGE_LABELS = {
  online_group: 'Online Group Classes',
  physical: 'Physical Group Classes',
  starter_group: 'Starter Group',
  standard: 'Standard Group',
  premium: 'Premium Group',
  ielts_intensive: 'IELTS Group Intensive',
  corporate: 'Corporate Group',
};

const STATUS_COLORS = {
  forming: C.warning,
  ready: '#8B5CF6',
  active: C.success,
  completed: C.grey,
};

const STATUS_LABELS = {
  forming: 'Forming',
  ready: '⚡ Ready to Schedule',
  active: 'Active',
  completed: 'Completed',
};

function StatusChip({ status }) {
  return (
    <span style={{
      display: 'inline-block', padding: '3px 10px', borderRadius: 20,
      fontSize: 12, fontWeight: 600,
      background: (STATUS_COLORS[status] || C.grey) + '20',
      color: STATUS_COLORS[status] || C.grey,
    }}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function AssignModal({ group, onClose, onDone }) {
  const [tutors, setTutors] = useState([]);
  const [selectedTutor, setSelectedTutor] = useState('');
  const [startDate, setStartDate] = useState('');
  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/api/admin/tutors').then(r => setTutors(r.data.tutors || r.data || [])).catch(() => {});
  }, []);

  const toggleSlot = (dayOfWeek, hour) => {
    const key = `${dayOfWeek}-${hour}`;
    const exists = schedule.find(s => s.day_of_week === dayOfWeek && s.hour === hour);
    if (exists) {
      setSchedule(schedule.filter(s => !(s.day_of_week === dayOfWeek && s.hour === hour)));
    } else {
      const maxSlots = group.sessions_per_week || 3;
      if (schedule.length >= maxSlots) {
        toast.error(`Max ${maxSlots} slots per week for this group`);
        return;
      }
      setSchedule([...schedule, { day_of_week: dayOfWeek, hour }]);
    }
  };

  const isSelected = (dayOfWeek, hour) => schedule.some(s => s.day_of_week === dayOfWeek && s.hour === hour);

  const submit = async () => {
    if (!selectedTutor) return toast.error('Please select a tutor');
    if (schedule.length === 0) return toast.error('Please select at least one weekly time slot');
    if (!startDate) return toast.error('Please select a start date');
    if (schedule.length < (group.sessions_per_week || 1)) {
      toast(`Note: ${schedule.length} slot(s) selected, ${group.sessions_per_week} needed per week`, { icon: '⚠️' });
    }
    setLoading(true);
    try {
      await api.post(`/api/admin/groups/${group.id}/assign`, {
        tutor_id: parseInt(selectedTutor),
        schedule,
        start_date: startDate,
      });
      toast.success(`Group scheduled! All sessions created for ${group.member_count} students.`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to schedule group');
    } finally {
      setLoading(false);
    }
  };

  const hours = [8,9,10,11,12,13,14,15,16,17,18,19,20];

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:16, padding:28, width:600, maxWidth:'100%', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
        <h3 style={{ margin:'0 0 4px', fontSize:18, fontWeight:700, color:C.dark }}>Assign Tutor & Schedule</h3>
        <p style={{ margin:'0 0 20px', fontSize:13, color:C.grey }}>
          {PACKAGE_LABELS[group.package] || group.package} · {group.cefr_level} · {group.member_count}/{group.max_size} students · {group.sessions_per_week} sessions/week · {group.total_sessions} total sessions
        </p>

        {/* Tutor Select */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize:13, fontWeight:600, color:C.dark, display:'block', marginBottom:6 }}>Select Tutor</label>
          <select value={selectedTutor} onChange={e => setSelectedTutor(e.target.value)} style={{ width:'100%', padding:'10px 12px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:14, color:C.dark }}>
            <option value="">— Choose a tutor —</option>
            {tutors.map(t => (
              <option key={t.id} value={t.id}>{t.first_name} {t.last_name}</option>
            ))}
          </select>
        </div>

        {/* Start Date */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize:13, fontWeight:600, color:C.dark, display:'block', marginBottom:6 }}>Course Start Date</label>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
            style={{ width:'100%', padding:'10px 12px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:14, color:C.dark }} />
        </div>

        {/* Weekly Schedule Grid */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize:13, fontWeight:600, color:C.dark, display:'block', marginBottom:8 }}>
            Weekly Schedule — select {group.sessions_per_week} slot(s) <span style={{ color:C.grey, fontWeight:400 }}>({schedule.length} selected)</span>
          </label>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead>
                <tr>
                  <th style={{ padding:'4px 8px', color:C.grey, fontWeight:600, textAlign:'left' }}>Time</th>
                  {DAYS.map(d => (
                    <th key={d} style={{ padding:'4px 8px', color:C.grey, fontWeight:600, textAlign:'center', minWidth:44 }}>{d}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {hours.map(h => (
                  <tr key={h}>
                    <td style={{ padding:'3px 8px', color:C.grey, fontWeight:500, whiteSpace:'nowrap' }}>{h}:00</td>
                    {DAYS.map((d, di) => {
                      const sel = isSelected(di, h);
                      return (
                        <td key={d} style={{ padding:'3px 4px', textAlign:'center' }}>
                          <button onClick={() => toggleSlot(di, h)} style={{
                            width:32, height:28, borderRadius:6,
                            border: sel ? 'none' : `1px solid ${C.border}`,
                            background: sel ? C.teal : '#fff',
                            cursor:'pointer', fontSize:10, fontWeight:600,
                            color: sel ? '#fff' : C.grey,
                          }}>
                            {sel ? '✓' : ''}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          <button onClick={onClose} style={{ padding:'10px 20px', border:`1px solid ${C.border}`, borderRadius:8, background:'#fff', cursor:'pointer', fontSize:14, color:C.dark }}>
            Cancel
          </button>
          <button onClick={submit} disabled={loading} style={{ padding:'10px 24px', border:'none', borderRadius:8, background:C.teal, color:'#fff', cursor:loading?'not-allowed':'pointer', fontSize:14, fontWeight:600, opacity:loading?0.7:1 }}>
            {loading ? 'Scheduling…' : `Schedule All ${group.total_sessions} Sessions`}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReassignModal({ student, currentGroupId, onClose, onDone }) {
  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/api/admin/groups/list-for-assign')
      .then(r => setGroups((r.data.groups || []).filter(g => g.id !== currentGroupId)))
      .catch(() => toast.error('Failed to load groups'))
      .finally(() => setLoading(false));
  }, [currentGroupId]);

  const save = async () => {
    if (!selected && selected !== 'none') return toast.error('Select a group');
    setSaving(true);
    try {
      const group_id = selected === 'none' ? null : parseInt(selected);
      await api.put(`/api/admin/students/${student.id}/assign-group`, { group_id });
      toast.success(group_id ? `${student.full_name} moved to Group ${selected}` : `${student.full_name} removed from group`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to reassign');
    } finally { setSaving(false); }
  };

  const DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1100, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:16, padding:28, width:480, maxWidth:'100%', maxHeight:'80vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
        <h3 style={{ margin:'0 0 4px', fontSize:17, fontWeight:700, color:C.dark }}>Move Student to Group</h3>
        <p style={{ margin:'0 0 16px', fontSize:13, color:C.grey }}>Moving: <strong>{student.full_name}</strong></p>
        {loading ? (
          <div style={{ textAlign:'center', padding:24, color:C.grey }}>Loading groups…</div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:20 }}>
            <button onClick={() => setSelected('none')} style={{
              padding:'12px 16px', borderRadius:10, textAlign:'left', cursor:'pointer',
              border: selected === 'none' ? `2px solid ${C.error}` : `1px solid ${C.border}`,
              background: selected === 'none' ? '#FEF2F2' : C.bg,
            }}>
              <div style={{ fontSize:13, fontWeight:700, color:C.error }}>🚫 Remove from all groups</div>
              <div style={{ fontSize:11, color:C.grey, marginTop:2 }}>Student will be unassigned — no sessions created</div>
            </button>
            {groups.length === 0 && <div style={{ textAlign:'center', padding:16, color:C.grey, fontSize:13 }}>No other groups available</div>}
            {groups.map(g => (
              <button key={g.id} onClick={() => setSelected(String(g.id))} style={{
                padding:'12px 16px', borderRadius:10, textAlign:'left', cursor:'pointer',
                border: selected === String(g.id) ? `2px solid ${C.teal}` : `1px solid ${C.border}`,
                background: selected === String(g.id) ? C.teal+'10' : C.bg,
              }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:4 }}>
                  <span style={{ fontSize:12, fontWeight:700, background:C.teal+'20', color:C.teal, padding:'2px 8px', borderRadius:10 }}>{g.cefr_level}</span>
                  <span style={{ fontSize:13, fontWeight:600, color:C.dark }}>{PACKAGE_LABELS[g.package] || g.package}</span>
                  <span style={{ fontSize:11, color: g.status==='active'?C.success:g.status==='ready'?'#8B5CF6':C.warning, fontWeight:600, marginLeft:'auto' }}>{g.status}</span>
                </div>
                <div style={{ fontSize:12, color:C.grey }}>
                  {g.member_count}/{g.max_size} students
                  {g.tutor_name && ` · ${g.tutor_name}`}
                  {g.scheduled && g.schedule?.length > 0 && ` · ${g.schedule.map(s => DAYS[s.day_of_week]).join('/')} ${g.schedule[0]?.hour}:00`}
                </div>
                {g.status === 'active' && <div style={{ fontSize:11, color:C.success, marginTop:3 }}>✓ Sessions will be auto-created from today</div>}
              </button>
            ))}
          </div>
        )}
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          <button onClick={onClose} style={{ padding:'8px 20px', border:`1px solid ${C.border}`, borderRadius:8, background:'#fff', cursor:'pointer', fontSize:14 }}>Cancel</button>
          <button onClick={save} disabled={saving || (!selected)} style={{ padding:'8px 24px', border:'none', borderRadius:8, background:C.teal, color:'#fff', cursor:saving||!selected?'not-allowed':'pointer', fontSize:14, fontWeight:600, opacity:saving||!selected?0.6:1 }}>
            {saving ? 'Moving…' : 'Confirm Move'}
          </button>
        </div>
      </div>
    </div>
  );
}

function MembersModal({ group, onClose, onMemberMoved }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reassignStudent, setReassignStudent] = useState(null);

  const loadMembers = () => {
    setLoading(true);
    api.get(`/api/admin/groups/${group.id}/members`)
      .then(r => setMembers(r.data.members || []))
      .catch(() => toast.error('Failed to load members'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadMembers(); }, [group.id]);

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:16, padding:28, width:560, maxWidth:'100%', maxHeight:'80vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
        <h3 style={{ margin:'0 0 4px', fontSize:18, fontWeight:700, color:C.dark }}>Group Members</h3>
        <p style={{ margin:'0 0 16px', fontSize:13, color:C.grey }}>
          {PACKAGE_LABELS[group.package] || group.package} · {group.cefr_level} · {members.length}/{group.max_size} students
        </p>
        {loading ? (
          <div style={{ textAlign:'center', padding:32, color:C.grey }}>Loading…</div>
        ) : members.length === 0 ? (
          <div style={{ textAlign:'center', padding:32, color:C.grey }}>No members yet</div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {members.map(m => (
              <div key={m.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 14px', background:C.bg, borderRadius:10 }}>
                <div style={{ width:36, height:36, borderRadius:'50%', background:C.teal+'20', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <span style={{ fontSize:14, fontWeight:700, color:C.teal }}>{m.full_name?.charAt(0)}</span>
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:14, fontWeight:600, color:C.dark }}>{m.full_name}</div>
                  <div style={{ fontSize:12, color:C.grey }}>{m.email}</div>
                  {m.tutor_preference_name && (
                    <div style={{ fontSize:11, color:C.teal, marginTop:2 }}>Prefers: {m.tutor_preference_name}</div>
                  )}
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ fontSize:12, fontWeight:600, color:C.dark }}>{m.cefr_level}</div>
                    <div style={{ fontSize:11, color: m.payment_status === 'paid' ? C.success : C.warning }}>{m.payment_status}</div>
                  </div>
                  <button
                    onClick={() => setReassignStudent(m)}
                    title="Move to another group"
                    style={{ padding:'5px 10px', border:`1px solid ${C.border}`, borderRadius:6, background:'#fff', cursor:'pointer', fontSize:12, color:C.dark, whiteSpace:'nowrap' }}>
                    ↔ Move
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        <div style={{ marginTop:16, textAlign:'right' }}>
          <button onClick={onClose} style={{ padding:'8px 20px', border:`1px solid ${C.border}`, borderRadius:8, background:'#fff', cursor:'pointer', fontSize:14 }}>Close</button>
        </div>
      </div>

      {reassignStudent && (
        <ReassignModal
          student={reassignStudent}
          currentGroupId={group.id}
          onClose={() => setReassignStudent(null)}
          onDone={() => { setReassignStudent(null); loadMembers(); onMemberMoved && onMemberMoved(); }}
        />
      )}
    </div>
  );
}

export default function AdminGroups() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assignGroup, setAssignGroup] = useState(null);
  const [membersGroup, setMembersGroup] = useState(null);

  const loadGroups = useCallback(async () => {
    try {
      const r = await api.get('/api/admin/groups');
      setGroups(r.data.groups || []);
    } catch {
      toast.error('Failed to load groups');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadGroups(); }, [loadGroups]);

  const forming = groups.filter(g => g.status === 'forming');
  const ready = groups.filter(g => g.status === 'ready');
  const active = groups.filter(g => g.status === 'active');
  const completed = groups.filter(g => g.status === 'completed');

  const GroupCard = ({ group }) => (
    <div style={{
      background:'#fff', borderRadius:14, padding:20,
      border: group.status === 'ready' ? `2px solid ${'#8B5CF6'}` : `1px solid ${C.border}`,
      boxShadow: group.status === 'ready' ? '0 0 0 4px #8B5CF620' : '0 1px 4px rgba(0,0,0,0.06)',
      transition:'box-shadow 0.2s',
    }}>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:12 }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
            <span style={{ fontSize:13, fontWeight:700, background:C.teal+'20', color:C.teal, padding:'2px 10px', borderRadius:20 }}>
              {group.cefr_level}
            </span>
            <span style={{ fontSize:12, color:C.grey, fontWeight:500 }}>
              {PACKAGE_LABELS[group.package] || group.package}
            </span>
          </div>
          <StatusChip status={group.status} />
        </div>
        <div style={{ textAlign:'right' }}>
          <div style={{ fontSize:26, fontWeight:800, color: group.member_count >= group.max_size ? C.success : C.dark }}>
            {group.member_count}<span style={{ fontSize:16, fontWeight:400, color:C.grey }}>/{group.max_size}</span>
          </div>
          <div style={{ fontSize:12, color:C.grey }}>students</div>
        </div>
      </div>

      <div style={{ display:'flex', gap:16, marginBottom:14, fontSize:12, color:C.grey }}>
        <span>📚 {group.total_sessions} sessions</span>
        <span>📅 {group.sessions_per_week}×/week</span>
        {group.tutor_name && <span>👤 {group.tutor_name}</span>}
        {group.course_end_date && <span>🏁 {group.course_end_date}</span>}
      </div>

      {group.schedule && group.schedule.length > 0 && (
        <div style={{ marginBottom:12, display:'flex', flexWrap:'wrap', gap:6 }}>
          {group.schedule.map((s, i) => (
            <span key={i} style={{ fontSize:11, background:C.bg, border:`1px solid ${C.border}`, borderRadius:6, padding:'2px 8px', color:C.dark }}>
              {DAYS[s.day_of_week]} {s.hour}:00
            </span>
          ))}
        </div>
      )}

      <div style={{ display:'flex', gap:8 }}>
        <button
          onClick={() => setMembersGroup(group)}
          style={{ flex:1, padding:'8px 0', border:`1px solid ${C.border}`, borderRadius:8, background:'#fff', cursor:'pointer', fontSize:13, color:C.dark, fontWeight:500 }}
        >
          👥 {group.member_count} Members
        </button>
        {(group.status === 'ready' || group.status === 'forming') && !group.scheduled && (
          <button
            onClick={() => setAssignGroup(group)}
            style={{ flex:1, padding:'8px 0', border:'none', borderRadius:8, background: group.status === 'ready' ? '#8B5CF6' : C.teal, color:'#fff', cursor:'pointer', fontSize:13, fontWeight:700 }}
          >
            ⚡ Assign Tutor
          </button>
        )}
        {group.status === 'active' && (
          <button
            onClick={() => setAssignGroup(group)}
            style={{ flex:1, padding:'8px 0', border:`1px solid ${C.border}`, borderRadius:8, background:'#fff', cursor:'pointer', fontSize:12, color:C.grey }}
          >
            ✏️ Reschedule
          </button>
        )}
      </div>
    </div>
  );

  const Section = ({ title, items, emptyMsg }) => (
    <div style={{ marginBottom: 32 }}>
      <h3 style={{ fontSize:15, fontWeight:700, color:C.dark, marginBottom:12 }}>
        {title} <span style={{ fontSize:13, fontWeight:400, color:C.grey }}>({items.length})</span>
      </h3>
      {items.length === 0 ? (
        <div style={{ padding:'24px', background:C.bg, borderRadius:12, textAlign:'center', color:C.grey, fontSize:13 }}>{emptyMsg}</div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))', gap:16 }}>
          {items.map(g => <GroupCard key={g.id} group={g} />)}
        </div>
      )}
    </div>
  );

  return (
    <Layout role="tenant_admin">
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 20px' }}>
        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
          <div>
            <h1 style={{ fontSize:24, fontWeight:800, color:C.dark, margin:0 }}>Class Groups</h1>
            <p style={{ fontSize:14, color:C.grey, margin:'4px 0 0' }}>
              Students are auto-assigned to groups when payment is confirmed
            </p>
          </div>
          <div style={{ display:'flex', gap:16 }}>
            {[
              { label:'Forming', count: forming.length, color: C.warning },
              { label:'Ready', count: ready.length, color: '#8B5CF6' },
              { label:'Active', count: active.length, color: C.success },
            ].map(s => (
              <div key={s.label} style={{ textAlign:'center', padding:'8px 16px', background:'#fff', borderRadius:10, border:`1px solid ${C.border}` }}>
                <div style={{ fontSize:20, fontWeight:800, color:s.color }}>{s.count}</div>
                <div style={{ fontSize:11, color:C.grey }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign:'center', padding:60, color:C.grey }}>
            <UserGroupIcon style={{ width:40, height:40, margin:'0 auto 12px', display:'block' }} />
            Loading groups…
          </div>
        ) : groups.length === 0 ? (
          <div style={{ textAlign:'center', padding:60, background:'#fff', borderRadius:16, border:`1px solid ${C.border}` }}>
            <UserGroupIcon style={{ width:48, height:48, color:C.grey, margin:'0 auto 12px', display:'block' }} />
            <div style={{ fontSize:17, fontWeight:600, color:C.dark, marginBottom:8 }}>No groups yet</div>
            <div style={{ fontSize:14, color:C.grey }}>Groups are created automatically when students complete payment for group packages.</div>
          </div>
        ) : (
          <>
            {ready.length > 0 && <Section title="⚡ Ready to Schedule" items={ready} emptyMsg="" />}
            {forming.length > 0 && <Section title="🔄 Forming" items={forming} emptyMsg="No groups forming" />}
            {active.length > 0 && <Section title="✅ Active" items={active} emptyMsg="No active groups" />}
            {completed.length > 0 && <Section title="🏁 Completed" items={completed} emptyMsg="No completed groups" />}
          </>
        )}
      </div>

      {assignGroup && (
        <AssignModal
          group={assignGroup}
          onClose={() => setAssignGroup(null)}
          onDone={() => { setAssignGroup(null); loadGroups(); }}
        />
      )}
      {membersGroup && (
        <MembersModal
          group={membersGroup}
          onClose={() => setMembersGroup(null)}
          onMemberMoved={loadGroups}
        />
      )}
    </Layout>
  );
}
